import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getAccountsDataView,
    getCurrentAccountState,
} from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import setTableColumns from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetTableColumns";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import setVisibleColumnNames from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetVisibleColumnNames";
import {
    getProductListDataView,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductGridCard from "@insite/content-library/Widgets/ProductList/ProductListProductGridCard";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addToCart from "@tSchaedlerYesco/Store/Pages/Cart/Handlers/AddToCart";
import sortBy from "lodash/sortBy";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showCompare = "showCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    productListDefaultView = "productListDefaultView",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: {
                isLoading,
                isSearchPage,
                view,
                productFilters: { pageCategoryId },
                parameter,
            },
        },
    } = state;
    const {
        websiteSettings: { includeRealTimeInventoryAndPricingForSeo },
        productSettings: { productLoadStyle },
    } = getSettingsCollection(state);
    return {
        isLoading,
        productsDataView: getProductListDataView(state),
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        view: view || ownProps.fields.productListDefaultView,
        isSearchPage,
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        brandFacets: getProductListDataViewProperty(state, "brandFacets"),
        productLineFacets: getProductListDataViewProperty(state, "productLineFacets"),
        includeRealTimeInventoryAndPricingForSeo,
        pageCategoryId,
        productLoadStyle,
        page: parameter?.page ?? 1,
        pageSize: parameter?.pageSize ?? 0,
        totalItemCount: getProductListDataViewProperty(state, "pagination")?.totalItemCount ?? 0,
        isLoadMore: isLoadMore(state),
        account: getCurrentAccountState(state).value,
        accountsDataView: getAccountsDataView(state),
    };
};

const mapDispatchToProps = {
    setView,
    addToCart,
    setTableColumns,
    setVisibleColumnNames,
    addProductFilters,
    loadAccount,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasCategoryContext;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    loader?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    loadMoreWrapper?: InjectableCss;
    loadMoreButton?: ButtonPresentationProps;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    formWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 40%;
                        `,
                        css`
                            margin-top: 40%;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
            > div:first-child[class^="FormFieldStyle"] {
                width: 48%;
                margin-right: 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: 100%;
                            `,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "minMax",
                    )}
            }
            > div[class^="FormFieldStyle"] {
                &:nth-child(2) {
                    width: 48%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 100%;
                                `,
                                css`
                                    width: 100%;
                                `,
                                null,
                                null,
                            ],
                            "minMax",
                        )}
                }
            }
            > div[class^="FormFieldStyle"] {
                &:nth-child(3) {
                    width: 48%;
                    margin-right: 10px;
                    margin-top: 10px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 100%;
                                `,
                                css`
                                    width: 100%;
                                `,
                                null,
                                null,
                            ],
                            "minMax",
                        )}
                }
            }
            > div[class^="FormFieldStyle"] {
                &:nth-child(4) {
                    width: 48%;
                    margin-right: 10px;
                    margin-top: 10px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 100%;
                                `,
                                css`
                                    width: 100%;
                                `,
                                null,
                                null,
                            ],
                            "minMax",
                        )}
                }
            }
            > div[class^="FormFieldStyle"] {
                &:nth-child(5) {
                    width: 48%;
                    margin-top: 10px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 100%;
                                `,
                                css`
                                    width: 100%;
                                `,
                                null,
                                null,
                            ],
                            "minMax",
                        )}
                    > div[class^="FormInputWrapper"] {
                        > textarea[data-test-selector^="comments"] {
                            resize: none;
                        }
                    }
                }
            }
            > div {
                > button[data-test-selector^="submitNoProductFoundForm"] {
                    margin: 10px 0;
                }
            }
        `,
    },
    loader: {
        css: css`
            z-index: 0;
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            padding: 0 50px;
            margin: 20px 0;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noProductsText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
                [data-test-selector^="productDescriptionLink"] {
                    span {
                        font-size: 16px;
                        font-weight: 600;
                        color: #353432;
                    }
                }
                div[data-test-selector="productListProductImage"] {
                    height: 250px;
                }
                [data-test-selector^="productImage"] {
                    max-width: 150px;
                    max-height: 150px;
                    margin: auto;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                        object-position: center top;
                    }
                }
                [data-test-selector^="productPriceSavingsMessage"] {
                    color: rgb(27, 51, 88);
                    font-size: 14px;
                    font-weight: bold;
                }
                [data-test-selector="productPrice_unitNetPrice"] {
                    color: #3666b1;
                    font-size: 18px;
                    font-weight: bold;
                }
                [data-test-selector="productPrice_unitOfMeasureLabel"] {
                    display: none;
                }
                [data-test-selector="brandLink"] {
                    span {
                        font-size: 14px;
                    }
                }
                [data-test-selector^="actionsAddToList"] {
                    span {
                        color: #353432;
                    }
                }
                span[class="add"] {
                    background: rgb(54, 102, 177);
                    padding: 0px 5px;
                    border-radius: 50%;
                    margin-right: 5px;
                    line-height: 0;
                    font-size: 12px;
                    color: rgb(255, 255, 255) !important;
                }
                [color^="success"] {
                    background: transparent;
                    padding: 0;
                    div[class^="GridItemStyle"] {
                        padding: 15px;
                    }
                    span[class^="IconWrapper"] {
                        background: #008061;
                        border-radius: 50%;
                        padding: 2px;
                        height: 16px;
                        width: 16px;
                        svg {
                            height: 12px;
                            width: 12px;
                            color: #fff;
                        }
                    }
                    [data-test-selector^="availabilityMessage"] {
                        font-size: 14px;
                        margin-top: -5px;
                    }
                }
                [color^="danger"] {
                    background: transparent;
                    padding: 0;
                    div[class^="GridItemStyle"] {
                        padding: 15px;
                    }
                    span[class^="IconWrapper"] {
                        background: #008061;
                        border-radius: 50%;
                        padding: 2px;
                        height: 16px;
                        width: 16px;
                        svg {
                            height: 12px;
                            width: 12px;
                            color: #fff;
                        }
                    }
                    [data-test-selector^="availabilityMessage"] {
                        font-size: 14px;
                        margin-top: -5px;
                    }
                }

                [data-test-selector^="actionsAddToCart"] {
                    text-transform: uppercase;
                    background-color: #3b7e63;
                    color: #ffffff;
                    border: 1px solid #3b7e63;
                    height: 36px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 55%;
                                `,
                                css`
                                    width: 55%;
                                `,
                                null,
                                null,
                            ],
                            "minMax",
                        );
                    }}
                    &:hover {
                        background-color: #3b7e63;
                        color: #fff;
                        border: 1px solid #3b7e63;
                    }
                    span {
                        font-size: 14px;
                    }
                }
                [data-test-selector="availabilityByWarehouseLink"] {
                    span {
                        font-size: 14px;
                        color: #000;
                    }
                }

                [data-test-selector^="quantityBreakPricingLink"] {
                    span {
                        font-size: 14px;
                        color: #000;
                    }
                }

                .icon {
                    border: 2px solid #008061;
                    border-radius: 50%;
                    padding: 0px 5px;
                    margin-right: 5px;
                    font-size: 8px !important;
                    color: #008061 !important;
                    font-weight: 900;
                }
            `,
        },
        gridItem: {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
    loadMoreWrapper: {
        css: css`
            width: 100%;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    loadMoreButton: {
        variant: "secondary",
        css: css`
            margin: 0 10px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = listStyles;

const ProductListCardList = ({
    id,
    isLoading,
    isSearchPage,
    productsDataView,
    cartPageLink,
    view,
    fields,
    attributeTypeFacets,
    brandFacets,
    productLineFacets,
    includeRealTimeInventoryAndPricingForSeo,
    pageCategoryId,
    productLoadStyle,
    page,
    pageSize,
    totalItemCount,
    isLoadMore,
    setView,
    setTableColumns,
    setVisibleColumnNames,
    addToCart,
    addProductFilters,
    accountsDataView,
    loadAccount,
}: Props) => {
    // React.useEffect(() => {
    //     setView({ view });
    // }, []);

    React.useEffect(() => {
        if (!accountsDataView.value) {
            loadAccount({
                accountId: "current",
            });
        }
    }, [accountsDataView]);

    useEffect(() => {
        if (!attributeTypeFacets?.length && !brandFacets?.length && !productLineFacets?.length) {
            setTableColumns({ tableColumns: [] });
            setVisibleColumnNames({ visibleColumnNames: [] });
            return;
        }

        const columns = attributeTypeFacets
            ? sortBy(
                  attributeTypeFacets,
                  o => o.sortOrder,
                  o => o.name,
              )
            : [];
        if (brandFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Brand",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        if (productLineFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Product Line",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        setTableColumns({ tableColumns: columns });
        setVisibleColumnNames({ visibleColumnNames: columns?.slice(0, 3).map(o => o.name) || [] });
    }, [pageCategoryId, attributeTypeFacets?.length, brandFacets?.length, productLineFacets?.length]);

    const manufacturerNameRequired = siteMessage("Manufacture_Name_Required");
    const catalogNumberRequired = siteMessage("catalog_Number_required");
    const commentsRequired = siteMessage("comments_required");
    const quantityRequired = siteMessage("quantity_required");
    const [manufacturerName, setManufacturerName] = useState("");
    const [manufacturerNameError, setmanufacturerNameError] = React.useState(manufacturerNameRequired);
    const [catalogNumber, setCatalogNumber] = useState("");
    const [catalogNumberError, setcatalogNumberError] = React.useState(catalogNumberRequired);
    const [upcNumber, setUpcNumber] = useState("");
    const [quantity, setquantity] = useState("");
    const [quantityError, setquantityError] = React.useState(quantityRequired);
    const [comments, setComments] = useState("");
    const [commentsError, setcommentsError] = React.useState(commentsRequired);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [loadSpinner, setloadSpinner] = React.useState(false);

    const observer: React.MutableRefObject<any> = useRef();
    const itemsLeft = totalItemCount - page * pageSize;

    const infiniteScrollTriggerRef = useCallback(
        node => {
            if (productLoadStyle !== "InfiniteScroll" || getIsWebCrawler() || isLoading) {
                return;
            }

            if (observer.current) {
                observer.current.disconnect();
            }

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && itemsLeft > 0) {
                    addProductFilters({ page: page + 1 });
                }
            });

            if (node) {
                observer.current?.observe(node);
            }
        },
        [productLoadStyle, isLoading, itemsLeft],
    );

    const loadMoreClickHandler = () => {
        addProductFilters({ page: page + 1 });
    };

    if (isLoading && !productsDataView.value && !isLoadMore) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;

    const manufacturerNameChangeHandler = (manufacturerName: string) => {
        if (manufacturerName) {
            validatemanufacturerName(manufacturerName);
            setManufacturerName(manufacturerName);
        }
    };

    const validatemanufacturerName = (title: string) => {
        const errorMessage = !title ? manufacturerNameRequired : "";
        setmanufacturerNameError(errorMessage);
        return !errorMessage;
    };

    const catalogNumberChangeHandler = (catalogNumber: string) => {
        if (catalogNumber) {
            setCatalogNumber(catalogNumber);
            validatecatalogNumber(catalogNumber);
        }
    };

    const validatecatalogNumber = (title: string) => {
        const errorMessage = !title ? catalogNumberRequired : "";
        setcatalogNumberError(errorMessage);
        return !errorMessage;
    };

    const quantityChangeHandler = (quantity: string) => {
        if (quantity) {
            validatequantityNumber(quantity);
            setquantity(quantity);
        }
    };

    const validatequantityNumber = (quantity: string) => {
        const errorMessage = !quantity ? quantityRequired : "";
        setquantityError(errorMessage);
        return !errorMessage;
    };

    const upcNumberChangeHandler = (upcNumber: string) => {
        if (upcNumber) {
            setUpcNumber(upcNumber);
        }
    };

    const commentsChangeHandler = (comments: string) => {
        if (comments) {
            validatecomments(comments);
            setComments(comments);
        }
    };

    const validatecomments = (title: string) => {
        const errorMessage = !title ? commentsRequired : "";
        setcommentsError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        if (manufacturerNameError || catalogNumberError || commentsError || quantityError) {
            return !(manufacturerNameError || catalogNumberError || commentsError || quantityError);
        }
        return !(manufacturerNameError || catalogNumberError || commentsError || quantityError);
    };

    const isSubmitEnabled = validateSubmitEnabled();

    const addDefaultProductToCart = async () => {
        const defaultProductId = productsDataView.properties?.defaultProductId;

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }
        setloadSpinner(true);

        let lineItemNote = "";
        if (manufacturerName) {
            lineItemNote += `Manufacturer Name: ${manufacturerName} `;
        }
        if (catalogNumber) {
            lineItemNote += `Catalog Number: ${catalogNumber} `;
        }
        if (upcNumber) {
            lineItemNote += `UPC Number: ${upcNumber} `;
        }
        if (comments) {
            lineItemNote += `Comments: ${comments} `;
        }

        await addToCart({
            productId: defaultProductId,
            // eslint-disable-next-line radix
            qtyOrdered: parseInt(quantity),
            unitOfMeasure: "EA",
            notes: lineItemNote,
            onSuccess: (response: any) => {
                redirectToCartPage(response);
            },
        });
    };

    const redirectToCartPage = (data: any) => {
        if (data) {
            window.location.href = cartPageLink?.url ? cartPageLink?.url : "/Cart";
            setloadSpinner(false);
        }
    };

    if (products.length === 0) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    <Typography {...styles.noProductsText}>{siteMessage("ProductNotFound_Note").toString()}</Typography>
                </StyledWrapper>
                <LoadingOverlay loading={loadSpinner && !isLoadMore}>
                    <StyledWrapper {...styles.formWrapper}>
                        <TextField
                            label={translate("Manufacturer Name")}
                            value={manufacturerName}
                            data-test-selector="manufacturer_Name"
                            onChange={e => manufacturerNameChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && manufacturerNameError}
                            required
                        />
                        <TextField
                            label={translate("Catalog")}
                            value={catalogNumber}
                            data-test-selector="catalog"
                            onChange={e => catalogNumberChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && catalogNumberError}
                            required
                        />
                        <TextField
                            label={translate("Add Quantity")}
                            value={quantity}
                            type="number"
                            min={1}
                            data-test-selector="add_Quantity"
                            onChange={e => quantityChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && quantityError}
                            required
                        />
                        <TextField
                            label={translate("UPC (If Known)")}
                            value={upcNumber}
                            data-test-selector="upcNumber"
                            onChange={e => upcNumberChangeHandler(e.currentTarget.value)}
                            error={false}
                            required={false}
                        />
                        <TextArea
                            label={translate("Desc/Comments")}
                            value={comments}
                            data-test-selector="comments"
                            onChange={e => commentsChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && commentsError}
                            required
                        />
                        <StyledWrapper>
                            <Button
                                onClick={addDefaultProductToCart}
                                data-test-selector="submitNoProductFoundForm"
                                disabled={isSubmitted && !isSubmitEnabled}
                            >
                                {translate("Submit")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {!isLoading && isLoadMore && productLoadStyle === "InfiniteScroll" && itemsLeft > 0 && (
                        <div ref={infiniteScrollTriggerRef} data-test-selector="infiniteScrollTrigger"></div>
                    )}
                    {!isLoading && isLoadMore && productLoadStyle === "LoadMore" && itemsLeft > 0 && (
                        <StyledWrapper {...styles.loadMoreWrapper}>
                            <Button
                                {...styles.loadMoreButton}
                                data-test-selector="loadMoreButton"
                                onClick={loadMoreClickHandler}
                            >
                                {translate("Load More")}
                            </Button>
                        </StyledWrapper>
                    )}
                    {isLoading && isLoadMore && (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    )}
                </LoadingOverlay>
            </StyledWrapper>
        );
    }

    const renderProducts = (children: ReactNode) => {
        return products.map((product, idx) => (
            <ProductListProductContext product={product} idx={idx} key={product.id}>
                {children}
            </ProductListProductContext>
        ));
    };

    const overriddenProps = { ...fields };
    if (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) {
        overriddenProps.showAvailability = false;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading}>
                <Hidden below="md">
                    <CardList extendedStyles={styles.cardList} data-test-selector={`productListCards_${view}`}>
                        {renderProducts(
                            view === "List" ? (
                                <CardContainer extendedStyles={styles.cardContainerStyles}>
                                    <ProductListProductCard {...overriddenProps} />
                                </CardContainer>
                            ) : (
                                <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                                    <ProductListProductGridCard {...overriddenProps} />
                                </CardContainerMultiColumn>
                            ),
                        )}
                    </CardList>
                </Hidden>
                <Hidden above="sm">
                    <CardList extendedStyles={styles.cardList} data-test-selector="cardListProductsNarrow">
                        {renderProducts(
                            <CardContainer extendedStyles={styles.cardContainerStyles}>
                                <ProductListProductCard {...overriddenProps} />
                            </CardContainer>,
                        )}
                    </CardList>
                </Hidden>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(ProductListCardList)),
    definition: {
        group: "Product List",
        displayName: "Card List",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "List",
                fieldType: "General",
                sortOrder: 10,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                ],
            },
            {
                name: fields.showImageBadges,
                displayName: "Show Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Show Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;
